import { Col, Flex, notification, Row } from "antd";

import "./OwnerDashboard.scss";
import { OwnerDashboardSalesCard } from "./owner-dashboard-sales-card/OwnerDashboardSalesCard";
import { OwnerDashboardProjectCard } from "./owner-dashboard-project-card/OwnerDashboardProjectCard";
import { SalesTargetTimePeriods } from "users/components";
import { useSalesInfo } from "sales/queries";
import { useEffect, useState } from "react";
import { useBoundStore } from "store";
import variables from "../../../../_variables.scss";
import { NotesList, NotesDrawer } from "notes/components";
import { useProjectsInfo } from "projects/queries";
import { useNotes } from "notes/queries";

export const OwnerDashboard = () => {
  const { currentOrganization } = useBoundStore();

  // Consolidated sales data hook — single request for all sales data
  const {
    data: salesInfo,
    error: salesError,
    isSuccess: salesInfoSuccess,
    refetch: refetchSalesInfo,
  } = useSalesInfo(currentOrganization?.id);

  const {
    data: projectsInfo,
    error: projectsInfoError,
    isSuccess: projectsInfoSuccess,
  } = useProjectsInfo(currentOrganization?.id);

  const {
    data: notesQueryData,
    error: notesError,
    isSuccess: notesSuccess,
    isLoading: notesLoading,
    fetchNextPage: fetchMoreNotes,
    hasNextPage: notesHasMore,
    refetch: refetchNotes,
  } = useNotes(undefined, { enabled: !!currentOrganization?.id });

  const notesData = notesQueryData?.pages.flatMap(p => p.items) ?? [];

  const [api, notificationContextHolder] = notification.useNotification();
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);

  // All data must be ready before showing content
  const allDataReady = salesInfoSuccess && notesSuccess && projectsInfoSuccess;

  const onSalesTargetTimePeriodChange = () => {
    refetchSalesInfo();
    refetchNotes();
  };

  useEffect(() => {
    if (salesError) {
      api.error({
        message: "Error loading sales info",
        description: (salesError as Error)?.message,
      });
    }

    if (projectsInfoError) {
      api.error({
        message: "Error loading projects info",
        description: projectsInfoError?.message,
      });
    }

    if (notesError) {
      api.error({
        message: "Error loading notes",
        description: (notesError as Error)?.message,
      });
    }
  }, [salesError, projectsInfoError, notesError, api]);

  return (
    <Flex vertical gap={variables.largeGap}>
      {notificationContextHolder}
      <Flex>
        <SalesTargetTimePeriods
          name='sales-target-time-periods'
          onChange={onSalesTargetTimePeriodChange}
        />
      </Flex>
      <Row gutter={{ xs: 4, sm: 8, md: 16, lg: 32 }} justify={"space-between"}>
        <Col span={17}>
          <OwnerDashboardSalesCard
            loading={!allDataReady}
            summaryCards={salesInfo?.salesCards}
            targetVsActualSales={salesInfo?.targetVsActualSales}
            salesByUtility={salesInfo?.salesByUtility}
            salesLeaderboard={salesInfo?.salesLeaderboard}
          />
        </Col>
        <Col span={7}>
          <NotesList
            title="Notes"
            showCard
            showProjectName
            data={notesData}
            hasMore={notesHasMore ?? false}
            onLoadMore={() => fetchMoreNotes()}
            loading={!allDataReady || notesLoading}
            onRefetch={refetchNotes}
            limit={5}
            onViewAll={() => setNotesDrawerOpen(true)}
          />
        </Col>
      </Row>
      <OwnerDashboardProjectCard loading={!allDataReady} projectsInfo={projectsInfo} />
      <NotesDrawer
        open={notesDrawerOpen}
        onClose={() => setNotesDrawerOpen(false)}
        data={notesData}
        hasMore={notesHasMore ?? false}
        onLoadMore={() => fetchMoreNotes()}
        loading={notesLoading}
        onRefetch={refetchNotes}
      />
    </Flex>
  );
};
